import * as THREE from "three";
import { SELECTABLE_LAYER } from "../../../layers";
import type { ElementGeometry, ElementSceneData } from "./ElementSceneDefinition";

type SceneElement = ElementSceneData["Elements"][number];
type PolylineGeometry = Extract<ElementGeometry, { Kind: "Polyline" }>;
type SimpleGeometry = Extract<ElementGeometry, { Type: string }>;
type Vec3 = { X: number; Y: number; Z: number };

import { createMesh } from "./MeshFactory";

export async function load(file: File): Promise<THREE.Object3D | null> {
	try {
		const text = await file.text();
		const data = JSON.parse(text);
		return fromObject(data);
	} catch (e) {
		console.warn("Failed to parse file or create object:", e);
		return null;
	}
}

export function fromObject(data: ElementSceneData, options?: { skipMeshRotation?: boolean }): THREE.Object3D | null {
	const skipMeshRotation = options?.skipMeshRotation ?? false;

	if (!data.Elements) {
		console.warn("No Elements found in data");
		return processSingleMesh(data);
	}

	if (Array.isArray(data.Elements)) {
		const group = new THREE.Group();
		group.name = data.Name || `ElementScene_ ${Date.now()}`;

		// Set high renderOrder to ensure ElementScene renders on top of Mapbox buildings
		group.renderOrder = 1000;
		group.matrixAutoUpdate = true;

		// Only set group GUID if it exists in the imported data
		group.userData = {
			Name: data.Name,
		};
		group.layers.enable(SELECTABLE_LAYER);

		if (data.Guid) {
			group.userData.guid = data.Guid;
			group.userData.Guid = data.Guid;
		}

		if (data.Properties) {
			group.userData.Properties = data.Properties;
		}

		let processedCount: number = 0;
		let skippedCount: number = 0;

		for (let i = 0; i < data.Elements.length; i++) {
			const element = data.Elements[i];

			const geometry = element.Geometry;
			if (!geometry) {
				console.warn(`Element ${i + 1} (${element.Name}) is missing geometry!`);
				skippedCount++;
				continue;
			}

			try {
				if ("Kind" in geometry && geometry.Kind === "Polyline" && Array.isArray(geometry.Points)) {
					const result = processPolylineElement(group, element, geometry, skipMeshRotation);
					if (result) {
						processedCount++;
					} else {
						skippedCount++;
					}
				} else if ("Vertices" in geometry && "Indices" in geometry) {
					const geometryData = {
						Vertices: geometry.Vertices,
						Indices: geometry.Indices,
					};

					const normals = element.Normals;
					createAndAddMesh(group, element, geometryData, normals, skipMeshRotation);
					processedCount++;
				} else if ("Type" in geometry && ["Box", "Sphere", "Cylinder", "Plane"].includes(geometry.Type)) {
					// Handle simple geometry types by converting to mesh data
					const meshData = convertSimpleGeometryToMesh(geometry);
					if (meshData) {
						createAndAddMesh(group, element, meshData, undefined, skipMeshRotation);
						processedCount++;
					} else {
						console.warn(`Element ${i + 1} (${element.Name}) failed to convert ${geometry.Type} geometry`);
						skippedCount++;
					}
				} else {
					console.warn(`Element ${i + 1} (${element.Name}) has unsupported geometry format`);
					skippedCount++;
				}
			} catch (e) {
				console.error(`Failed to process element ${i + 1} (${element.Name}):`, e);
				skippedCount++;
			}
		}

		console.log({ processedCount, skippedCount });

		if (group.children.length > 0) {
			// Ensure all children have normal renderOrder to participate in depth testing
			// Keep normal depth testing so ElementScene behaves like other scene objects
			group.traverse((child) => {
				if (child instanceof THREE.Mesh) {
					child.renderOrder = 0;
					// Materials keep default depthTest=true and depthWrite=true
					// This ensures ElementScene objects interact properly with depth
					if (child.material instanceof THREE.Material) {
						child.material.needsUpdate = true;
					}
				}
			});
			return group;
		} else {
			console.warn("ElementSceneConverter: No valid meshes could be created from Elements.");
			return null;
		}
	}

	return processSingleMesh(data);
}

function processSingleMesh(data: Record<string, unknown>): THREE.Object3D | null {
	if (Array.isArray(data.vertices) && Array.isArray(data.indices)) {
		const normals = Array.isArray(data.normals) ? data.normals : [];
		const mesh = createMesh(data.vertices, data.indices, normals);
		addUserData(mesh, data);
		return mesh || null;
	}

	console.warn("ElementSceneConverter: Unrecognized mesh format");
	return null;
}

function processPolylineElement(group: THREE.Group, element: SceneElement, geometry: PolylineGeometry, skipMeshRotation: boolean = false): boolean {
	try {
		const shape = new THREE.Shape();
		const points = geometry.Points.map((pt) => new THREE.Vector2(pt.X, pt.Y));

		if (points.length < 3) {
			console.warn(`Skipping ${element.Name}: not enough points for a polygon`);
			return false;
		}

		shape.moveTo(points[0].x, points[0].y);
		for (let i = 1; i < points.length; i++) {
			shape.lineTo(points[i].x, points[i].y);
		}

		const first = points[0];
		const last = points[points.length - 1];
		if (first.x !== last.x || first.y !== last.y) {
			shape.lineTo(first.x, first.y);
		}

		const shapeGeometry = new THREE.ShapeGeometry(shape);
		// Only rotate if not in Mapbox context (Mapbox handles coordinate conversion)
		if (!skipMeshRotation) {
			shapeGeometry.rotateX(-Math.PI / 2);
		}

		const posAttr = shapeGeometry.getAttribute("position");
		const idxAttr = shapeGeometry.getIndex();
		const normAttr = shapeGeometry.getAttribute("normal");

		const geometryData = {
			Vertices: Array.from({ length: posAttr.count }, (_, i) => ({
				X: posAttr.getX(i),
				Y: posAttr.getY(i),
				Z: posAttr.getZ(i),
			})),
			Indices: idxAttr ? Array.from(idxAttr.array) : [],
		};

		const normals = normAttr
			? Array.from({ length: normAttr.count }, (_, i) => ({
					X: normAttr.getX(i),
					Y: normAttr.getY(i),
					Z: normAttr.getZ(i),
				}))
			: undefined;

		createAndAddMesh(group, element, geometryData, normals, skipMeshRotation);
		return true;
	} catch (e) {
		console.error("Failed to convert polyline to polygon for", element.Name, e);
		return false;
	}
}

function convertSimpleGeometryToMesh(geometry: SimpleGeometry): { Vertices: Array<Vec3>; Indices: Array<number> } | null {
	try {
		switch (geometry.Type) {
			case "Box": {
				const w = geometry.Width || 1;
				const h = geometry.Height || 1;
				const d = geometry.Depth || 1;
				return {
					Vertices: [
						// Front face
						{ X: -w / 2, Y: -h / 2, Z: d / 2 },
						{ X: w / 2, Y: -h / 2, Z: d / 2 },
						{ X: w / 2, Y: h / 2, Z: d / 2 },
						{ X: -w / 2, Y: h / 2, Z: d / 2 },
						// Back face
						{ X: -w / 2, Y: -h / 2, Z: -d / 2 },
						{ X: w / 2, Y: -h / 2, Z: -d / 2 },
						{ X: w / 2, Y: h / 2, Z: -d / 2 },
						{ X: -w / 2, Y: h / 2, Z: -d / 2 },
					],
					Indices: [
						0,
						1,
						2,
						0,
						2,
						3, // Front
						4,
						6,
						5,
						4,
						7,
						6, // Back
						4,
						5,
						1,
						4,
						1,
						0, // Bottom
						3,
						2,
						6,
						3,
						6,
						7, // Top
						4,
						0,
						3,
						4,
						3,
						7, // Left
						1,
						5,
						6,
						1,
						6,
						2, // Right
					],
				};
			}
			case "Plane": {
				const w = geometry.Width || 1;
				const h = geometry.Height || 1;
				return {
					Vertices: [
						{ X: -w / 2, Y: 0, Z: -h / 2 },
						{ X: w / 2, Y: 0, Z: -h / 2 },
						{ X: w / 2, Y: 0, Z: h / 2 },
						{ X: -w / 2, Y: 0, Z: h / 2 },
					],
					Indices: [0, 1, 2, 0, 2, 3],
				};
			}
			default:
				console.warn(`Simple geometry type '${geometry.Type}' not yet implemented`);
				return null;
		}
	} catch (e) {
		console.error(`Error converting ${geometry.Type} geometry:`, e);
		return null;
	}
}

function addUserData(object: THREE.Object3D, data: Record<string, unknown>) {
	object.userData = object.userData || {};
	if (data.Guid) {
		object.userData.guid = data.Guid;
		object.userData.Guid = data.Guid;
	}
	object.userData.Name = data.Name;
	object.userData.Properties = data.Properties;
	object.userData.isElementScene = true; // Mark as ElementScene for depth reservation

	// Enable raycasting layer so the object can be picked by RaycastService
	object.layers.enable(SELECTABLE_LAYER);
}

function createAndAddMesh(
	group: THREE.Group,
	element: SceneElement,
	geometryData: { Vertices: Array<Vec3>; Indices: Array<number> },
	normals?: Array<Vec3>,
	skipMeshRotation: boolean = false,
): void {
	const flatVertices = geometryData.Vertices?.flatMap((v) => [v.X, v.Y, v.Z]) ?? [];
	const indices = geometryData.Indices ?? [];
	const flatNormals = normals?.flatMap((n) => [n.X, n.Y, n.Z]) ?? [];

	try {
		const mesh = createMesh(flatVertices, indices, flatNormals, element.Color);
		mesh.name = element.Name ?? "Unnamed";
		addUserData(mesh, element);
		// Only rotate if not in Mapbox context (Mapbox handles coordinate conversion)
		if (!skipMeshRotation) {
			mesh.rotateX(-Math.PI / 2);
		}

		// Use normal renderOrder (0) so ElementScene participates in proper depth testing
		// Mapbox middle slot allows proper depth interaction with Mapbox buildings
		mesh.renderOrder = 0;
		mesh.matrixAutoUpdate = true;

		// Configure material for proper depth testing with Mapbox
		// We want ElementScene to participate in normal depth testing, not override it
		if (mesh.material instanceof THREE.Material) {
			// Ensure material has proper defaults (depthTest and depthWrite are true by default)
			// This allows proper bidirectional occlusion with Mapbox buildings
			// NO polygon offset - we want accurate depth testing, not forced rendering order
			mesh.material.depthTest = true;
			mesh.material.depthWrite = true;
			mesh.material.needsUpdate = true;
		}

		group.add(mesh);
	} catch (e) {
		console.error("Error creating mesh for", element.Name, e);
	}
}
